import React, { ChangeEvent, useCallback, useContext, useState } from 'react';
import DatePicker from './DatePicker';
import FilterPlateFormContext from '../../../contexts/FilterPlateFormContext';
import useDateFilterValues from '../../../hooks/useDateFilterValues';
import doesArrayContainDateOption from '../doesArrayContainDateOption';
import filterTypeCheckers from '../filterTypeCheckers';
import getIdentifier from '../../../getIdentifier';
import DATE_FIELDS from '../../../hooks/useMetricMatrix/constants';

const useEditingDateFilter = () => {
  const { drillDown } = useContext(FilterPlateFormContext);
  if (!drillDown) {
    return undefined;
  }
  if (filterTypeCheckers.isDateFilter(drillDown)) {
    return drillDown;
  }

  return undefined;
};

const DatePickerContainer = () => {
  const editingDateFilter = useEditingDateFilter();
  const [searchText, setSearchText] = useState('');
  const { field, fieldType, isEditing, dataset, onDrillDownConfirmed } =
    useContext(FilterPlateFormContext);
  const [alreadySelectedOptions] = useState<DateOption[]>(
    editingDateFilter ? editingDateFilter.dateRangeValues || [] : [],
  );
  const [dateRangeValues, setDateRangeValues] = useState<DateOption[]>(
    editingDateFilter ? editingDateFilter.dateRangeValues || [] : [],
  );
  const [exists, setExists] = useState<boolean | undefined>(
    editingDateFilter ? editingDateFilter.exists : undefined,
  );

  const { options } = useDateFilterValues(field, 'dateText');
  const optionsWithoutPreSelected = alreadySelectedOptions
    ? options.filter(
        (o) => !doesArrayContainDateOption(alreadySelectedOptions, o),
      )
    : options;
  const filteredOptions = (() => {
    if (searchText === '') {
      return optionsWithoutPreSelected;
    }

    return optionsWithoutPreSelected.filter((o) =>
      o.label.toLowerCase().includes(searchText.toLowerCase()),
    );
  })();

  const onSearchTextChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const onKeywordSelected = (dateRange: DateOption) => {
    setDateRangeValues([dateRange]);
  };

  const onKeywordDeselected = () => {
    setDateRangeValues([]);
  };

  const onConfirm = useCallback(() => {
    if (!field) {
      return;
    }
    if (!(fieldType === 'dateText' || fieldType === 'date')) {
      return;
    }
    const newDrillDown: DateFilter = (() => {
      const base = {
        field,
        fieldType,
        dataset,
        dateRangeValues:
          dateRangeValues.length > 0 ? dateRangeValues : undefined,
        mode: 'editing' as 'editing',
        exists,
      };
      if (editingDateFilter) {
        return {
          ...editingDateFilter,
          ...base,
        };
      }

      return {
        id: getIdentifier(undefined, true),
        ...base,
      };
    })();
    onDrillDownConfirmed(newDrillDown);
  }, [
    dataset,
    dateRangeValues,
    editingDateFilter,
    field,
    fieldType,
    exists,
    onDrillDownConfirmed,
  ]);

  const clearSelectedOptions = () => {
    setDateRangeValues([]);
  };

  const isSearching = searchText !== '';
  const selectedOptions = dateRangeValues || [];
  const canClearSelectedOptions =
    !isSearching &&
    alreadySelectedOptions &&
    selectedOptions.filter((o) => alreadySelectedOptions.includes(o)).length >
      1;

  const setMustExist = useCallback((newValue: boolean) => {
    if (!newValue) {
      setExists(undefined);
    }
    setExists(true);
  }, []);
  const setMustNotExist = useCallback((newValue: boolean) => {
    if (!newValue) {
      setExists(undefined);
    }
    setExists(false);
  }, []);
  const isExistsFiltersVisible = !!field && !DATE_FIELDS.includes(field);

  return (
    <DatePicker
      isSearching={isSearching}
      canClearSelectedOptions={!!canClearSelectedOptions}
      alreadySelectedOptions={alreadySelectedOptions}
      options={filteredOptions}
      selectedOptions={selectedOptions}
      searchText={searchText}
      onSearchTextChanged={onSearchTextChanged}
      onKeywordSelected={onKeywordSelected}
      onKeywordDeselected={onKeywordDeselected}
      onConfirm={onConfirm}
      isEditing={isEditing}
      clearSelectedOptions={clearSelectedOptions}
      isValid={dateRangeValues.length !== 0 || exists !== undefined}
      isExistsFiltersVisible={isExistsFiltersVisible}
      mustExist={exists !== undefined && exists}
      setMustExist={setMustExist}
      mustNotExist={exists !== undefined && !exists}
      setMustNotExist={setMustNotExist}
    />
  );
};

export default DatePickerContainer;
